import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import * as db from "@/lib/db.server";

const SAFE_COLUMNS =
  "id, title, slug, category_id, meta_title, meta_description, h1, excerpt, content, faq_json, internal_links_json, related_articles_json, related_search_links_json, schema_json, author_name, reviewer_name, medical_disclaimer, seo_score, status, index_status, canonical_url, published_at, last_updated_at, created_at, updated_at";

export const listCategories = createServerFn({ method: "GET" }).handler(async () => {
  const rows = await db.query(
    "SELECT id, name, slug, description, sort_order FROM public.blog_categories ORDER BY sort_order ASC",
  );
  return rows ?? [];
});

export const listPublishedArticles = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        category_slug: z.string().min(1).max(80).optional(),
        limit: z.number().int().min(1).max(50).default(12),
        offset: z.number().int().min(0).max(10_000).default(0),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    let categoryId: string | null = null;
    if (data.category_slug) {
      const cat = await db.one<{ id: string }>(
        "SELECT id FROM public.blog_categories WHERE slug = $1",
        [data.category_slug],
      );
      if (!cat) return { rows: [], total: 0, category: null };
      categoryId = cat.id;
    }
    // No count on 2.7M-row table. Cursor-style limit+1 detects hasMore.
    // PostgREST .range(offset, offset+limit) is inclusive on both ends, so it
    // fetched limit+1 rows; replicate with LIMIT limit+1.
    const params: unknown[] = ["published"];
    let where = "status = $1";
    if (categoryId) {
      params.push(categoryId);
      where += ` AND category_id = $${params.length}`;
    }
    params.push(data.limit + 1);
    const limitParam = `$${params.length}`;
    params.push(data.offset);
    const offsetParam = `$${params.length}`;
    const fetched = await db.query(
      `SELECT id, title, slug, category_id, excerpt, meta_description, author_name, reviewer_name, published_at, last_updated_at, seo_score
         FROM public.blog_articles
        WHERE ${where}
        ORDER BY published_at DESC NULLS LAST
        LIMIT ${limitParam} OFFSET ${offsetParam}`,
      params,
    );
    const hasMore = fetched.length > data.limit;
    return { rows: fetched.slice(0, data.limit), total: 0, hasMore, category: data.category_slug ?? null };
  });

export const getArticleBySlug = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(300) }).parse(input))
  .handler(async ({ data }) => {
    const article = await db.one<any>(
      `SELECT ${SAFE_COLUMNS} FROM public.blog_articles WHERE slug = $1 AND status = $2`,
      [data.slug, "published"],
    );
    if (!article) return null;
    const category = article.category_id
      ? await db.one(
          "SELECT id, name, slug FROM public.blog_categories WHERE id = $1",
          [article.category_id],
        )
      : null;
    return { article, category };
  });

export const listSitemapBlogUrls = createServerFn({ method: "GET" }).handler(async () => {
  const [articles, cats] = await Promise.all([
    db.query(
      `SELECT slug, last_updated_at, published_at
         FROM public.blog_articles
        WHERE status = $1
        ORDER BY published_at DESC
        LIMIT 50000`,
      ["published"],
    ),
    db.query("SELECT slug FROM public.blog_categories"),
  ]);
  return { articles: articles ?? [], categories: cats ?? [] };
});
